import {AxiosError, AxiosResponse } from "axios";
import { OrganizationWithStatistics } from "../models/OrganizationWithStatistics";
import { Paging } from "../models/Paging";
import useApi, { useStreamingApi } from "../services/ApiService";
import {OrganizationConfiguration} from "../models/OrganizationConfiguration/OrganizationConfiguration";
import {ImplementationType} from "../models/OrganizationConfiguration/ImplementationType";
import {Organization, OrganizationName, OrganizationWithSecret,} from "../models/Organization";
import {DropboxRefreshToken} from "../models/OrganizationConfiguration/DropboxRefreshToken";
import {MicrosoftRefreshToken} from "../models/OrganizationConfiguration/MicrosoftRefreshToken";
import { MigrationType } from "../models/Statistics/MigrationType";
import { RecalculationTrigger, MigrationQueueStatus, RecalculateFilesPrioritiesResult } from "../models/Recalculation";
import { ConflictReason } from "../models/ConflictReason";
import {
  OrgConflictResolutionPreflight,
  OrgConflictResolutionProgress,
  OrgConflictResolutionResult,
  OrgConflictResolvedFile,
  ResolveOrgConflictsRequest,
} from "../models/OrgConflictResolution";
import { extractApiErrorMessage, extractProblemDetailsMessage } from "../lib/apiError";
import { readNdjsonResult } from "../lib/ndjson";
import { NotFoundRetryCount } from "../models/OrgNotFoundRetry";
import { FlagCounts } from "../models/OrgFlagCounts";

export interface GetOrganizationsQueryOptions {
  assignedUserId?: string;
  searchPhrase?: string;
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
}

export class OrganizationsRepository {
  private api = useApi();
  private streamingApi = useStreamingApi();

  async getOrganizations(
    options: GetOrganizationsQueryOptions = {}
  ): Promise<{ data: Organization[]; paging: Paging | null }> {
    const {
      assignedUserId = null,
      searchPhrase = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
    } = options;
    try {
      const response = await this.api.get("/api/Organizations", {
        params: {
          AssignedUserId: assignedUserId,
          SearchPhrase: searchPhrase,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
        },
      });
      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader)
        paging = JSON.parse(paginationHeader || "");
      
      return { data, paging };
    } catch (error) {
      throw new Error("Failed to fetch organizations");
    }
  }
  
  async getOrganizationWithStatistics(id: string): Promise<OrganizationWithStatistics> {
    try {
      const response = await this.api.get(
        `/api/Organizations/${id}/statistics`,
        {
          headers: {
            'Organization-ID': this.api.defaults.headers.common['Organization-ID']
              ? this.api.defaults.headers.common['Organization-ID']
              : id,
          },
        }
        
      );
      return response.data;
    } catch (error) {
      // Handle error
      throw new Error(`Failed to fetch organization with id: ${id}`);
    }
  }

  async getOrganizationNavigationInfo(id: string): Promise<{
    intactId: string;
    name: string;
    assignedUserId: string;
  }> {
    try {
      const response = await this.api.get(
        `/api/Organizations/${id}/navigationInfo`, {
          headers: {'Organization-ID': id},
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch organization navigation info for organization with id: ${id}`);
    }
  }

  async getOrganizationNames(): Promise<OrganizationName[]> {
    try {
      const response = await this.api.get("/api/Organizations/names");
      return response.data as OrganizationName[];
    } catch (error) {
      throw new Error("Failed to fetch organization names");
    }
  }

  async getOrganizationClientIds(organizationId: string): Promise<string[]> {
    try {
      const response = await this.api.get(`/api/Organizations/clientIds`,);
      return response.data as string[];
    } catch (error) {
      throw new Error(`Failed to fetch organization client ids  of organization with id: ${organizationId}`);
    }
  }

  async getOrganizationDropboxRefreshTokens(organizationId: string): Promise<DropboxRefreshToken[]> {
    try {
      const response = await this.api.get(
        `/api/Organizations/${organizationId}/dropboxRefreshTokens`,
      );
      return response.data as DropboxRefreshToken[];
    } catch (error) {
      throw new Error(`Failed to fetch dropbox refresh tokens of organization with id: ${organizationId}`);
    }
  }

  async getOrganizationMicrosoftRefreshTokens(organizationId: string): Promise<MicrosoftRefreshToken[]> {
    try {
      const response = await this.api.get(
        `/api/Organizations/${organizationId}/microsoftRefreshTokens`,
      );
      return response.data as MicrosoftRefreshToken[];
    } catch (error) {
      throw new Error(`Failed to fetch Microsoft refresh tokens of organization with id: ${organizationId}`);
    }
  }

  async getOrganizationConfiguration(id: string): Promise<OrganizationConfiguration> {
    try {
      const response = await this.api.get(
        `/api/Organizations/${id}/configuration`
      );
      return response.data;
    } catch (error) {
      // Handle error
      throw new Error(`Failed to fetch organization configuration of organization with id: ${id}`);
    }
  }

  async getOrganizationImplementationType(id: string): Promise<ImplementationType> {
    try {
      const response = await this.api.get(
        `/api/Organizations/${id}/implementationType`
      );
      return response.data;
    } catch (error) {
      // Handle error
      throw new Error(`Failed to fetch organization implementation type of organization with id: ${id}`);
    }
  }

  async createOrganization(
    name: string,
    intactId: string,
    secret: string
  ): Promise<OrganizationWithSecret> {
    try {
      const response: AxiosResponse<OrganizationWithSecret> =
        await this.api.post("/api/Organizations/", {
          name: name,
          intactId: intactId,
          organizationSecret: secret,
        });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async RecalculateOrganizationStatistics(
    organizationId: string,
  ): Promise<void> {
    try {
      return await this.api.post(`/api/Organizations/${organizationId}/RecalculateStatistics`, {
        organizationId: organizationId,
      });
    } catch (error) {
      // Handle error
      throw new Error(`Failed to recalculate statistics of organization with id: ${organizationId}`);
    }
  }

  /**
   * Recalculates only the statistics a change has flagged dirty, plus the organization totals derived from them.
   * The background recalculation does this on its own interval; calling it directly is how a bulk mutation makes
   * the counts it changed truthful now instead of at the next pass.
   */
  async recalculateDirtyOrganizationStatistics(organizationId: string): Promise<void> {
    try {
      await this.api.post(`/api/Organizations/${organizationId}/RecalculateDirtyStatistics`);
    } catch (error) {
      throw new Error(
        extractApiErrorMessage(error, "Failed to refresh statistics for the organization")
      );
    }
  }

  async recalculateFilePriorities(
    organizationId: string,
    migrationTypes: MigrationType[],
    reason: string,
    trigger: RecalculationTrigger,
  ): Promise<RecalculateFilesPrioritiesResult> {
    try {
      const response = await this.api.post<RecalculateFilesPrioritiesResult>(
        "/api/Organizations/RecalculateFilesPriorities",
        {
          organizationIds: [organizationId],
          migrationTypes: migrationTypes,
          reason: reason,
          trigger: trigger,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to enqueue file priority recalculation.");
    }
  }

  /**
   * Exact per-reason unresolved-conflict counts for the current organization, plus the selected/residual split.
   * `selectedReasons` only affects that split — every occurring reason is always returned.
   */
  async getConflictResolutionPreflight(
    selectedReasons: ConflictReason[] = [],
    families: MigrationType[] = []
  ): Promise<OrgConflictResolutionPreflight> {
    try {
      const response = await this.api.get<OrgConflictResolutionPreflight>(
        "/api/Organizations/ConflictResolutionPreflight",
        {
          params: {
            SelectedReasons: selectedReasons,
            Families: families,
          },
          // Indexed keys (SelectedReasons[0]=…), which is what MVC's collection binder reads. Axios's
          // default for arrays is SelectedReasons[]=… — empty brackets, which the binder does not
          // recognise, so both collections arrive empty and every count silently collapses to zero
          // rather than erroring. Repeated keys (SelectedReasons=a&SelectedReasons=b) bind too; indexes
          // matches ProvidersRepository/TemplatesRepository.
          paramsSerializer: {
            indexes: true,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, "Failed to load org-wide conflict counts"));
    }
  }

  /** The current organization's Not Found files per type, for the org-wide retry. */
  async getNotFoundRetryCounts(): Promise<NotFoundRetryCount[]> {
    try {
      const response = await this.api.get<NotFoundRetryCount[]>("/api/Organizations/NotFoundRetryCounts");
      return response.data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, "Failed to load the Not Found file counts"));
    }
  }

  /** The current organization's flag-held entities per type: Blocked, by reason, and Acknowledged. */
  async getFlagCounts(): Promise<FlagCounts[]> {
    try {
      const response = await this.api.get<FlagCounts[]>("/api/Organizations/FlagCounts");
      return response.data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, "Failed to load the flag counts"));
    }
  }

  /**
   * Queues every Not Found file of the given types for retry, organization-wide. Returns the counts taken when it was
   * queued; the files change in the background, one parent at a time.
   */
  async retryNotFoundFiles(migrationTypes: MigrationType[], reason: string): Promise<NotFoundRetryCount[]> {
    try {
      const response = await this.api.post<NotFoundRetryCount[]>("/api/Organizations/RetryNotFoundFiles", {
        migrationTypes,
        reason,
      });
      return response.data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, "Failed to queue the Not Found files for retry"));
    }
  }

  /**
   * Resolves every unresolved conflict of the selected reasons org-wide. Reasons absent from `reasonActions` are
   * left untouched. Files come back QUEUED for re-upload — SharePoint only changes once the migration queue
   * reaches them.
   *
   * `onProgress` is called once per committed batch, from the newline-delimited response the server streams. The
   * operation runs for minutes on a large backlog, so those lines are what keep it alive: they reset the idle
   * timers of every intermediary, and they replace guesswork with counts the server has already committed.
   *
   * A stream that ends without its terminal line is reported as a failure, never as success. Files resolved before
   * the drop stay resolved, so the recovery is to reload the counts and re-run: the remainder is exactly what is
   * left unresolved.
   */
  async resolveConflictsOrgWide(
    request: ResolveOrgConflictsRequest,
    onProgress?: (progress: OrgConflictResolutionProgress) => void,
    signal?: AbortSignal
  ): Promise<OrgConflictResolutionResult> {
    const response = await this.streamingApi("/api/Organizations/ResolveConflicts", request, signal);

    if (!response.ok) {
      // Failed before the first line — validation, authorization, or the pre-flight concurrency check — so the
      // status is meaningful and the body is an ordinary problem-details document.
      const payload = await response.json().catch(() => null);
      throw new Error(extractProblemDetailsMessage(payload, "Failed to resolve conflicts org-wide"));
    }

    return readNdjsonResult<OrgConflictResolutionProgress, OrgConflictResolutionResult>(response, {
      onProgress,
      errorMessage: "The resolve failed after it had started.",
      droppedMessage:
        "The connection dropped before the resolve finished. Conflicts already resolved are committed — "
        + "reload the counts and run it again to resolve the rest.",
    });
  }

  /** The files one bulk conflict resolution touched, read from the audit entry's own snapshot rows. */
  async getConflictResolvedFiles(
    auditLogId: string,
    family: MigrationType | null = null,
    pageNumber = 1,
    pageSize = 100
  ): Promise<{ data: OrgConflictResolvedFile[]; paging: Paging | null }> {
    try {
      const response = await this.api.get<OrgConflictResolvedFile[]>(
        "/api/Organizations/ConflictResolvedFiles",
        {
          params: {
            AuditLogId: auditLogId,
            Family: family,
            "Paging.PageNumber": pageNumber,
            "Paging.PageSize": pageSize,
          },
        }
      );
      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader) {
        paging = JSON.parse(paginationHeader || "");
      }

      return { data, paging };
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, "Failed to load resolved files"));
    }
  }

  async getMigrationQueueStatus(organizationId: string): Promise<MigrationQueueStatus[]> {
    try {
      const response = await this.api.get<MigrationQueueStatus[]>(
        `/api/Organizations/${organizationId}/migration-queue-status`
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch migration queue status.");
    }
  }

  async patchOrganizationAssignedUser(
    organizationId: string,
    userId: string,
  ): Promise<void> {
    try {
      return await this.api.patch("/api/Organizations/PatchOrganizationAssignedUser", {
        organizationId: organizationId,
        userId: userId,
      });
    }  catch (error: unknown) {
      if ((error as AxiosError)?.isAxiosError) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 400) {
          const responseData = axiosError.response.data as {
            errors?: {
              organizationId?: string[],
              userId?: string[]
            }
          };

          if (responseData.errors?.organizationId?.[0])
            throw new Error(responseData.errors.organizationId[0]);
          
          if (responseData.errors?.userId?.[0])
            throw new Error(responseData.errors.userId[0]);
        }
      }

      throw new Error('Failed to patch assigned user for organization');
    }
  }

  async patchOrganizationConfiguration(
    organizationId: string,
    organizationConfiguration: OrganizationConfiguration,
    reason?: string
  ): Promise<OrganizationConfiguration> {
    try {
      return await this.api.patch(`/api/Organizations/PatchOrganizationConfiguration`, {
        organizationId: organizationId,
        configurationPatch: organizationConfiguration,
        reason: reason,
      });
    } catch (error) {
      // Handle error
      throw new Error(`Failed to patch configuration for organization`);
    }
  }

  async deleteOrganization(organizationId: string): Promise<void> {
    try {
      return await this.api.delete(`/api/Organizations/${organizationId}`);
    } catch (error) {
      // Handle error
      throw new Error("Failed to delete organization");
    }
  }

  /**
   * Reads the organization's client-enrolment secret. The server records every read in the organization's audit
   * log, so call it only when the user asks to see or use the secret.
   */
  /** A consent page path for the organization that works for seven days; making one is recorded in its audit log. */
  async createConsentLink(organizationId: string, kind: "sharepoint" | "dropbox" | "microsoft"): Promise<string> {
    const response = await this.api.post<{ path: string }>(
      `/api/Organizations/${organizationId}/consent-link`, null, { params: { kind } }
    );
    return response.data.path;
  }

  async getOrganizationSecret(organizationId: string): Promise<string> {
    const response = await this.api.get<{ organizationSecret: string }>(
      `/api/Organizations/${organizationId}/secret`
    );
    return response.data.organizationSecret;
  }

  async deleteDropboxRefreshToken(dropboxRefreshTokenId: string): Promise<void> {
    try {
      return await this.api.delete(`/api/Organizations/DeleteDropboxRefreshToken/${dropboxRefreshTokenId}`);
    } catch (error) {
      // Handle error
      throw new Error("Failed to delete dropbox refresh token");
    }
  }

  async deleteMicrosoftRefreshToken(microsoftRefreshTokenId: string): Promise<void> {
    try {
      return await this.api.delete(`/api/Organizations/DeleteMicrosoftRefreshToken/${microsoftRefreshTokenId}`);
    } catch (error) {
      // Handle error
      throw new Error("Failed to delete Microsoft refresh token");
    }
  }
}

export default OrganizationsRepository;
